<template>
  <HeaderTitle v-if="challenge" single :back-target-func="goToChallenge">
    <template #bottom>{{ challenge.title }}</template>
  </HeaderTitle>
  <BarChart
    v-if="chartData && challenge"
    :base-chart-data="chartData"
    :current-view="currentView"
    :is-pass-fail-challenge="challenge.isPassFailChallenge"
    :progress-value-type="progressValueType"
  />

  <div class="w-full flex flex-row justify-center mt-8">
    <div class="bg-bkk-50 rounded-full p-1 flex w-fit">
      <button
        :class="[
          ' py-1 rounded-full transition-colors duration-200 text-sm px-20',
          currentView === IntervalType.INTERVAL_WEEK
            ? 'bg-white border border-light-gray'
            : 'border-light-gray',
        ]"
        @click="currentView = IntervalType.INTERVAL_WEEK"
      >
        {{ t("challenge.statistic.week.label") }}
      </button>
      <button
        :class="[
          'py-1 rounded-full transition-colors duration-200 text-sm px-20',
          currentView === IntervalType.INTERVAL_MONTH
            ? 'bg-white border border-light-gray'
            : 'border-light-gray',
        ]"
        @click="currentView = IntervalType.INTERVAL_MONTH"
      >
        {{ t("challenge.statistic.month.label") }}
      </button>
    </div>
  </div>

  <HeadlineElement>
    {{ t("challenge.statistic.history.label") }}
  </HeadlineElement>

  <div
    v-if="chartDataList && challenge"
    class="grid grid-cols-1 sm:grid-cols-2 mt-8 gap-x-4 gap-y-2"
  >
    <HistoryTile
      v-for="entry in chartDataList"
      :id="
        routeName === 'competitiveChallengeStatistics'
          ? challenge.mappingId
          : challenge.id
      "
      :key="entry.id"
      :history-entry="entry"
      :placeholder="challenge.placeholder"
      :is-team-challenge="challengeType === ChallengeType.COMPETITIVE_CHALLENGE"
      :is-pass-fail-challenge="challenge.isPassFailChallenge"
      :is-competitive-challenge="routeName === 'competitiveChallengeStatistics'"
      :progress-value-type="progressValueType"
      :hint="challenge.hint"
      :end-date="challenge.endDate"
      :target-value="entry.targetValue"
      :limit="challenge.limit"
      @update-history="updateHistory"
    />
  </div>
</template>
<script setup lang="ts">
import BarChart from "../../../components/v2/challanges/BarChart.vue";
import { useRoute, useRouter } from "vue-router";
import { onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  fetchChartData,
  fetchChartDataList,
  fetchCustomChallenge,
} from "../../../api/v2/custom-challenge.ts";
import { useUserStore } from "../../../store/v2/user";
import HeaderTitle from "../../../components/v2/layout/header/HeaderTitle.vue";
import HeadlineElement from "../../../components/v2/layout/HeadlineElement.vue";
import HistoryTile from "../../../components/v2/challanges/HistoryTile.vue";
import { IntervalType } from "../../../enum/v2/intervalType.ts";
import { ChartDataDto } from "../../../dto/chart-data.dto.ts";
import { CustomChallengeDetailsDto } from "../../../dto/custom-challenge-details.dto.ts";
import {
  fetchCompetitiveChallenge,
  fetchCompetitiveChartData,
  fetchCompetitiveChartDataList,
} from "../../../api/v2/competitive-challenge.ts";
import { CompetitiveChallengeDetailsDto } from "../../../dto/competitive-challenge-details.dto.ts";
import { ChallengeType } from "../../../enum/v2/challengeType.ts";
import { useNotificationStore } from "../../../store/v2/notification";
import { notifyIfTargetValueJustAchieved } from "../../../plugins/ChallengeNotification";
import { ActivityType } from "../../../enum/v2/activityType.ts";
import { CompetitiveChallengeType } from "../../../enum/v2/competitive-challenge-type.ts";
import { ProgressValueType } from "../../../enum/v2/progress-value-type.ts";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const notificationStore = useNotificationStore();
const chartData = ref<ChartDataDto[] | null>(null);
const chartDataList = ref<ChartDataDto[] | null>(null);
const challenge = ref<
  CustomChallengeDetailsDto | CompetitiveChallengeDetailsDto | null
>(null);
const currentView = ref<
  IntervalType.INTERVAL_WEEK | IntervalType.INTERVAL_MONTH
>(IntervalType.INTERVAL_WEEK);
const routeName = route.name as string;
const challengeType = ref(null);
const progressValueType = computed<ProgressValueType | undefined>(() => {
  if (!challenge.value) {
    return undefined;
  }

  if (routeName === "competitiveChallengeStatistics") {
    return getCompetitiveProgressValueType(
      (challenge.value as CompetitiveChallengeDetailsDto)
        .competitiveChallengeType
    );
  }

  return getCustomProgressValueType(
    (challenge.value as CustomChallengeDetailsDto).activityType
  );
});

onMounted(async () => {
  await fetchData(currentView.value);
});

function goToChallenge() {
  if (routeName === "competitiveChallengeStatistics") {
    router.push({
      name: "competitiveChallengeDetail",
      params: { challengeId: route.params.challengeId },
    });
  } else {
    router.push({
      name: "customChallengeDetail",
      params: { challengeId: route.params.challengeId },
    });
  }
}

async function updateHistory(newValue: number): Promise<void> {
  const previousChallenge = hasTargetValueAchieved(challenge.value)
    ? { ...challenge.value }
    : null;

  await fetchData(currentView.value);

  if (previousChallenge && hasTargetValueAchieved(challenge.value)) {
    notifyIfTargetValueJustAchieved(
      notificationStore,
      t,
      previousChallenge,
      challenge.value
    );
  }
}

function hasTargetValueAchieved(
  challengeDetails:
    | CustomChallengeDetailsDto
    | CompetitiveChallengeDetailsDto
    | null
): challengeDetails is CustomChallengeDetailsDto {
  return Boolean(challengeDetails && "targetValueAchieved" in challengeDetails);
}

async function fetchData(
  interval: IntervalType.INTERVAL_WEEK | IntervalType.INTERVAL_MONTH
): Promise<void> {
  if (routeName === "competitiveChallengeStatistics") {
    challengeType.value = ChallengeType.COMPETITIVE_CHALLENGE;
    const res = await fetchCompetitiveChartData(
      userStore.externalUserId,
      interval,
      route.params.challengeId as string
    );

    chartData.value = res?.data ?? null;

    const listRes = await fetchCompetitiveChartDataList(
      userStore.externalUserId,
      route.params.challengeId as string
    );

    if (listRes) {
      chartDataList.value = listRes.data;
    } else {
      chartDataList.value = null;
    }

    const competitiveChallengeRes = await fetchCompetitiveChallenge(
      userStore.externalUserId,
      userStore.getClientCode,
      route.params.challengeId as string
    );

    if (competitiveChallengeRes) {
      challenge.value = competitiveChallengeRes.data;
    } else {
      challenge.value = null;
    }
  } else {
    challengeType.value = ChallengeType.CUSTOM_CHALLENGE;
    const res = await fetchChartData(
      userStore.externalUserId,
      interval,
      route.params.challengeId as string
    );

    chartData.value = res?.data ?? null;

    const listRes = await fetchChartDataList(
      userStore.externalUserId,
      route.params.challengeId as string
    );

    if (listRes) {
      chartDataList.value = listRes.data;
    } else {
      chartDataList.value = null;
    }

    const customChallengeRes = await fetchCustomChallenge(
      userStore.externalUserId,
      userStore.getClientCode,
      route.params.challengeId as string
    );

    if (customChallengeRes) {
      challenge.value = customChallengeRes.data;
    } else {
      challenge.value = null;
    }
  }
}

watch(currentView, (newVal: IntervalType) => {
  fetchData(newVal);
});

function getCompetitiveProgressValueType(
  competitiveChallengeType: CompetitiveChallengeType
): ProgressValueType | undefined {
  if (
    competitiveChallengeType ===
    CompetitiveChallengeType.COMPETITIVE_CHALLENGE_TYPE_STEPS
  ) {
    return ProgressValueType.Steps;
  }

  if (
    competitiveChallengeType ===
    CompetitiveChallengeType.COMPETITIVE_CHALLENGE_TYPE_BICYCLE
  ) {
    return ProgressValueType.Kilometers;
  }

  return undefined;
}

function getCustomProgressValueType(
  activityType: string
): ProgressValueType | undefined {
  if (activityType === ActivityType.CUSTOM_CHALLENGE_ACTIVITY_TYPE_STEPS) {
    return ProgressValueType.Steps;
  }

  if (activityType === ActivityType.CUSTOM_CHALLENGE_ACTIVITY_TYPE_BICYCLE) {
    return ProgressValueType.Kilometers;
  }

  return undefined;
}
</script>
